import {
  BriefcaseBusiness,
  CalendarPlus,
  Clock,
  FilePlus2,
  FileUp,
  FolderPlus,
  LifeBuoy,
  Megaphone,
  PackagePlus,
  Receipt,
  Send,
  Timer,
  UserPlus,
  UsersRound,
} from "lucide-react";

import { COMPANY_EXPENSES_PATHS } from "@/routes/company/routes.expenses";
import { COMPANY_RECRUITMENT_PATHS } from "@/routes/company/routes.recruitment";
import { PATHS } from "@/routes/paths";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import type { IQuickAction } from "./types";

/**
 * Every shortcut the Workspace dashboard can offer, in catalogue order (the
 * order the customize dialog lists what is not pinned).
 *
 * `key` is what the server stores, so it is permanent: rename the label, the
 * route or the icon freely, never the key. A key that disappears from here is
 * dropped from a saved bar on read, not migrated. Kebab-case only — the API
 * validates stored keys against `^[a-z0-9-]+$`.
 *
 * Each entry gates on `create`, not `view`: a shortcut to a form the user
 * cannot submit is worse than no shortcut.
 */
export const QUICK_ACTION_CATALOG: readonly IQuickAction[] = [
  {
    key: "add-employee",
    label: "company:widgets.add_employee",
    icon: UserPlus,
    to: PATHS.COMPANY.COMPANY_EMPLOYEES_PATHS.ADD,
    section: PLATFORM_SECTION_ENUM.EMPLOYEES,
    action: "create",
  },
  {
    key: "apply-leave",
    label: "company:widgets.apply_leave",
    icon: FilePlus2,
    to: PATHS.COMPANY.COMPANY_LEAVES_MANAGEMENT_PATHS.APPLY,
    section: PLATFORM_SECTION_ENUM.LEAVE_MANAGEMENT,
    action: "create",
  },
  {
    key: "regularize",
    label: "company:widgets.regularize",
    icon: Clock,
    to: PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.ADD_REGULARIZATION,
    section: PLATFORM_SECTION_ENUM.TIME_ATTENDANCE,
    action: "create",
  },
  {
    key: "add-overtime",
    label: "company:dashboard_quick_actions.actions.add_overtime",
    icon: Timer,
    to: PATHS.COMPANY.COMPANY_TIME_ATTENDANCE_PATHS.ADD_OVERTIME,
    section: PLATFORM_SECTION_ENUM.TIME_ATTENDANCE,
    action: "create",
  },
  {
    key: "add-asset",
    label: "company:widgets.add_asset",
    icon: PackagePlus,
    to: PATHS.COMPANY.COMPANY_ASSETS_PATHS.CREATE,
    section: PLATFORM_SECTION_ENUM.ASSETS,
    action: "create",
  },
  {
    key: "announcement",
    label: "company:widgets.announcement",
    icon: Megaphone,
    to: PATHS.COMPANY.COMPANY_ANNOUNCEMENTS_PATHS.NEW,
    section: PLATFORM_SECTION_ENUM.ANNOUNCEMENTS,
    action: "create",
  },
  {
    key: "new-event",
    label: "company:widgets.new_event",
    icon: CalendarPlus,
    to: PATHS.COMPANY.COMPANY_EVENT_PATHS.CREATE,
    section: PLATFORM_SECTION_ENUM.EVENTS,
    action: "create",
  },
  {
    key: "submit-expense",
    label: "company:dashboard_quick_actions.actions.submit_expense",
    icon: Receipt,
    to: COMPANY_EXPENSES_PATHS.CREATE,
    section: PLATFORM_SECTION_ENUM.EXPENSES,
    action: "create",
  },
  {
    key: "post-job",
    label: "company:dashboard_quick_actions.actions.post_job",
    icon: BriefcaseBusiness,
    to: COMPANY_RECRUITMENT_PATHS.JOB_FORM_NEW,
    section: PLATFORM_SECTION_ENUM.RECRUITMENT,
    action: "create",
  },
  {
    key: "request-document",
    label: "company:dashboard_quick_actions.actions.request_document",
    icon: Send,
    to: PATHS.COMPANY.COMPANY_DOCUMENTS_PATHS.REQUEST,
    section: PLATFORM_SECTION_ENUM.DOCUMENTS,
    action: "create",
  },
  {
    key: "upload-company-document",
    label: "company:dashboard_quick_actions.actions.upload_company_document",
    icon: FileUp,
    to: PATHS.COMPANY.COMPANY_DOCUMENTS_PATHS.COMPANY_DOCUMENT_NEW,
    section: PLATFORM_SECTION_ENUM.DOCUMENTS,
    action: "create",
  },
  {
    key: "new-team",
    label: "company:dashboard_quick_actions.actions.new_team",
    icon: UsersRound,
    to: PATHS.COMPANY.COMPANY_TEAMS_PATHS.CREATE,
    section: PLATFORM_SECTION_ENUM.TEAMS,
    action: "create",
  },
  {
    key: "new-project",
    label: "company:dashboard_quick_actions.actions.new_project",
    icon: FolderPlus,
    to: PATHS.COMPANY.COMPANY_PROJECTS_PATHS.CREATE,
    section: PLATFORM_SECTION_ENUM.PROJECTS,
    action: "create",
  },
  {
    key: "support-ticket",
    label: "company:dashboard_quick_actions.actions.support_ticket",
    icon: LifeBuoy,
    to: PATHS.COMPANY.COMPANY_HELP_SUPPORT_PATHS.NEW,
    section: PLATFORM_SECTION_ENUM.HELP_AND_SUPPORT,
    action: "create",
  },
];

/**
 * What a user who never customized sees: the six the dashboard has always
 * offered, then permission-filtered like everything else. A role that can
 * submit none of these gets the first permitted ones from the catalogue
 * instead, so the bar is never empty while there is something to put on it.
 */
export const DEFAULT_QUICK_ACTION_KEYS: readonly string[] = [
  "add-employee",
  "apply-leave",
  "regularize",
  "add-asset",
  "announcement",
  "new-event",
];

/** Above this the bar stops being a shortcut row and becomes a second menu. */
export const MAX_PINNED_QUICK_ACTIONS = 8;

/** Bumped only if the stored shape changes; the server just round-trips it. */
export const QUICK_ACTIONS_LAYOUT_VERSION = 1;

/** The API requires a size on every entry; a pinned action has none of its own. */
export const QUICK_ACTION_STORED_SIZE = "sm";
